/**
 * AuthShell — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Shared wrapper for the auth surface: /login, /signup, /forgot-password,
 * /reset-password/:token, /verify-email/:token, /accept-invite/:token, and
 * the onboarding wizard at /profile.
 *
 * Migrated from `--v2-*` legacy tokens to `--ds-*` design-system tokens.
 * Public API is unchanged (`title`, `subtitle`, `children`, `testId`,
 * `wide`) so every existing consumer keeps working without edits.
 */
import { Link } from "react-router-dom";
import ThemeToggle from "@/components/v2/ThemeToggle";
import SkipToContent from "@/components/a11y/SkipToContent";
import { APP_NAME } from "@/theme";

export default function AuthShell({ title, subtitle, children, testId, wide = false }) {
  return (
    <div
      data-testid={testId}
      data-page-outer="auth-cluster"
      className="min-h-screen overflow-x-hidden bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <SkipToContent />
      <header className="border-b border-[var(--ds-color-border)] bg-[var(--ds-color-surface)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="auth-shell-wordmark"
            className="inline-flex items-baseline gap-1 text-[15px] font-semibold text-[var(--ds-color-ink)] hover:opacity-90 transition-opacity"
            style={{
              fontFamily: "var(--ds-font-display)",
              letterSpacing: "-0.01em",
            }}
          >
            <span>{APP_NAME}</span>
            <span
              aria-hidden="true"
              className="ml-1 inline-block h-1.5 w-1.5 rounded-[var(--ds-radius-pill)] bg-[var(--ds-color-accent)]"
            />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main
        id="main-content"
        tabIndex={-1}
        className={`mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20 ${wide ? "max-w-2xl" : "max-w-md"}`}
      >
        <h1
          className="text-[28px] md:text-[36px] leading-tight text-[var(--ds-color-ink)]"
          style={{
            fontFamily: "var(--ds-font-display)",
            letterSpacing: "-0.02em",
            fontWeight: 600,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 text-[16px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            {subtitle}
          </p>
        )}
        <div className="mt-10">{children}</div>
      </main>
    </div>
  );
}
